import React, { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { sendAIChat } from '../../services/user';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

const AIAssistant = () => {
    const [messages, setMessages] = useState<{role: 'user'|'ai', content: string}[]>([
        { role: 'ai', content: 'Hello! I am your AI Business Assistant. How can I help you analyze your sellers or optimize your store today?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const userId = localStorage.getItem('ud-user') || '';

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setIsLoading(true);

        try {
            const res = await sendAIChat(userMsg, userId);
            setMessages(prev => [...prev, { role: 'ai', content: res.response }]);
        } catch(err) {
            toast.error("Failed to connect to AI");
            setMessages(prev => [...prev, { role: 'ai', content: "Sorry, I am having trouble connecting to the server right now." }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-6 h-[calc(100vh-64px)] flex flex-col">
            <div className="mb-6 flex items-center">
                <SparklesIcon className="w-8 h-8 text-green-600 mr-3" />
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">AI Business Assistant</h1>
                    <p className="text-gray-600">Powered by Gemini</p>
                </div>
            </div>

            <div className="flex-1 bg-white shadow rounded-lg flex flex-col overflow-hidden">
                <div className="flex-1 p-4 overflow-y-auto space-y-4">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] rounded-lg p-4 ${msg.role === 'user' ? 'bg-green-600 text-white rounded-br-none' : 'bg-gray-100 text-gray-800 rounded-bl-none prose prose-sm max-w-none'}`}>
                                {msg.role === 'user' ? (
                                    msg.content
                                ) : (
                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                )}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="bg-gray-100 text-gray-800 rounded-lg rounded-bl-none p-4">
                                <span className="animate-pulse">Thinking...</span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t bg-gray-50">
                    <form onSubmit={handleSend} className="flex space-x-2">
                        <input 
                            type="text" 
                            className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                            placeholder="Ask about your top sellers, price optimization, etc..."
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            disabled={isLoading}
                        />
                        <button 
                            type="submit" 
                            disabled={isLoading || !input.trim()}
                            className="bg-green-600 text-white p-2 rounded-full hover:bg-green-700 disabled:opacity-50 transition"
                        >
                            <PaperAirplaneIcon className="w-6 h-6" />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AIAssistant;
